/**
 * Single source of truth for status → Badge **variant** across V2 (colors only).
 * Visible status text stays as returned by the API — see {@link ./statusDisplay.js getStatusDisplayLabel}.
 * Visual intent: green = success, yellow-amber (warning) = processing / in-flight, red = failed.
 */

export function normalizeStatusKey(status) {
  if (status == null || status === "") return "";
  const s = String(status).trim();
  if (!s || s === "—" || s === "-") return "";
  return s.toLowerCase().replace(/[\s.-]+/g, "_");
}

/** Explicit overrides; keys are normalized via normalizeStatusKey. */
const VARIANT_BY_KEY = {
  // positive
  active: "success",
  verified: "success",
  completed: "success",
  complete: "success",
  done: "success",
  approved: "success",
  approve: "success",
  accepted: "success",
  reject: "error",
  released: "success",
  enabled: "success",
  success: "success",
  paid: "success",
  cleared: "success",
  refunded: "success",
  settled: "success",
  captured: "success",
  succeeded: "success",
  payment_completed: "success",
  payment_success: "success",
  txn_success: "success",
  transaction_success: "success",
  live: "success",
  published: "success",
  publish: "success",
  ok: "success",
  valid: "success",
  confirmed: "success",
  authenticated_success: "success",
  otp_authentication_success: "success",
  open: "success",

  // negative / terminal failure
  suspended: "error",
  failed: "error",
  fail: "error",
  rejected: "error",
  declined: "error",
  denied: "error",
  cancelled: "error",
  canceled: "error",
  blocked: "error",
  expired: "error",
  revoked: "error",
  void: "error",
  error: "error",
  authentication_fail: "error",
  otp_authentication_fail: "error",
  authentication_failure: "error",

  // inactive / inactive-style (grey)
  inactive: "neutral",
  in_active: "neutral",
  unverified: "neutral",
  draft: "neutral",
  unknown: "neutral",
  na: "neutral",
  n_a: "neutral",
  null: "neutral",
  none: "neutral",
  closed: "neutral",
  not_assigned: "neutral",

  // in-flight / attention
  not_approved: "warning",
  pending: "warning",
  inprocess: "warning",
  in_process: "warning",
  processing: "warning",
  initiated: "warning",
  hold: "warning",
  holding: "warning",
  partial: "warning",
  reopened: "warning",
  reopen: "warning",
  review: "warning",
  reviewing: "warning",
  waiting: "warning",
  queued: "warning",
  submitted: "warning",
  inprogress: "warning",
  progress: "warning",
  refund: "warning",

  running: "warning",
  in_flight: "warning",
  inflight: "warning",
  syncing: "warning",
  loading: "warning",
  settling: "warning",
  clearing: "warning",
  validating: "warning",
  scheduled: "warning",
  dispatched: "warning",
  debit_pending: "warning",

  unsuccessful: "error",
  failure: "error",
};

/**
 * @param {unknown} status Raw status from API
 * @returns {"success"|"error"|"warning"|"neutral"}
 */
export function getStatusVariant(status) {
  const key = normalizeStatusKey(status);
  if (!key) return "neutral";
  if (VARIANT_BY_KEY[key] !== undefined) return VARIANT_BY_KEY[key];

  if (
    key.endsWith("_success") ||
    (key.includes("success") && !key.includes("fail") && !key.includes("unsuccess"))
  ) {
    return "success";
  }
  if (
    key.endsWith("_fail") ||
    key.endsWith("_failed") ||
    key.includes("fail") ||
    key.includes("reject") ||
    key.includes("unsuccess")
  ) {
    return "error";
  }
  if (
    key.includes("pending") ||
    key.includes("hold") ||
    key.includes("process") ||
    key.includes("init") ||
    key.includes("progress") ||
    key.includes("wait") ||
    key.includes("sync") ||
    key.includes("queue") ||
    (key.length > 4 && key.endsWith("ing") && !key.includes("fail") && !key.includes("string"))
  ) {
    return "warning";
  }
  return "neutral";
}
